import type { CSSProperties } from "react";
import type { StoryGapQuestion, StoryReview } from "./App";

type StoryReviewProps = {
  storyReview: StoryReview;
  onBackToInterview: () => void;
  onStartGap: (gap: StoryGapQuestion) => void;
  onSkipGap: (gap: StoryGapQuestion) => void;
  onReviewBookPlan: () => void;
  startingStoryGapId?: number | null;
  skippingStoryGapId?: number | null;
  loading?: boolean;
};

const coverageLabels: Record<string, string> = {
  childhood: "Childhood",
  family: "Family",
  relationships: "Relationships",
  work: "Work and purpose",
  turning_points: "Turning points",
  values: "Values and lessons",
  legacy: "Legacy message",
};

const coverageStatusLabels: Record<string, string> = {
  missing: "Missing",
  medium: "Medium",
  strong: "Strong",
};

const SHOW_DEBUG_STORY_REVIEW =
  localStorage.getItem("SHOW_DEBUG_TIMINGS") === "true" ||
  localStorage.getItem("DEBUG_STORY_REVIEW") === "true";

export default function StoryReviewView({
  storyReview,
  onBackToInterview,
  onStartGap,
  onSkipGap,
  onReviewBookPlan,
  startingStoryGapId,
  skippingStoryGapId,
  loading,
}: StoryReviewProps) {
  const completeness = Math.max(
    0,
    Math.min(100, storyReview.readiness.completeness_percent)
  );
  const bookPlanLabel = storyReview.readiness.book_plan_available
    ? "Continue to Book Plan"
    : "Continue to Book Plan Anyway";
  const counts = storyReview.all_gap_counts;
  const suggestedCount = counts.open + counts.in_progress;
  const progressSummary = [
    `${counts.answered} answered`,
    `${counts.skipped} skipped`,
    `${suggestedCount} suggested`,
  ].join(" · ");
  const visibleQuestions = storyReview.open_questions.filter((gap) =>
    ["open", "in_progress"].includes(gap.status)
  );

  return (
    <div style={styles.storyReviewContainer}>
      <div style={styles.storyReviewHeader}>
        <h2 style={styles.storyReviewTitle}>Your Story Progress</h2>
        <p style={styles.storyReviewSubtitle}>
          Here’s what we’ve gathered so far, plus a few suggested questions
          that could make your story richer.
        </p>
      </div>

      <section style={styles.storyReviewCard}>
        <div style={styles.progressHeading}>
          <span>Story progress</span>
          <strong>{completeness}% complete</strong>
        </div>
        <div
          role="progressbar"
          aria-label="Story progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={completeness}
          style={styles.progressOuter}
        >
          <div
            style={{
              ...styles.progressInner,
              width: `${completeness}%`,
            }}
          />
        </div>
        <p style={styles.readinessMessage}>
          {storyReview.readiness.message}
        </p>
        <p style={styles.progressSummary}>{progressSummary}</p>
        {SHOW_DEBUG_STORY_REVIEW && !!counts.archived && (
          <p style={styles.debugMutedText}>
            {counts.archived} archived duplicate
            {counts.archived === 1 ? "" : "s"}
          </p>
        )}
      </section>

      <section style={styles.storyReviewCard}>
        <h3 style={styles.sectionTitle}>Story so far</h3>
        {storyReview.story_so_far.length > 0 ? (
          <ul style={styles.storyList}>
            {storyReview.story_so_far.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : (
          <p style={styles.mutedCopy}>
            We’re still gathering the first pieces of your story.
          </p>
        )}
      </section>

      <section style={styles.storyReviewCard}>
        <h3 style={styles.sectionTitle}>Suggested questions</h3>
        <p style={styles.optionalQuestionCopy}>
          These questions are optional. They are here to help make the final
          book richer.
        </p>
        {visibleQuestions.length > 0 ? (
          <div style={styles.storyGapList}>
            {visibleQuestions.map((gap) => {
              const isStarting = startingStoryGapId === gap.id;
              const isSkipping = skippingStoryGapId === gap.id;
              const isInProgress = gap.status === "in_progress";

              return (
                <article key={gap.id} style={styles.storyGapCard}>
                  <div style={styles.storyGapHeading}>
                    <h4 style={styles.storyGapTitle}>{gap.title}</h4>
                    {isInProgress && (
                      <span style={styles.startedBadge}>Started</span>
                    )}
                  </div>
                  <p style={styles.storyGapQuestion}>{gap.question}</p>
                  {gap.rationale && (
                    <p style={styles.storyGapRationale}>
                      {gap.rationale}
                    </p>
                  )}

                  <div style={styles.storyGapActions}>
                    <button
                      type="button"
                      style={styles.primaryButton}
                      onClick={() => onStartGap(gap)}
                      disabled={loading || isStarting || isSkipping}
                    >
                      {isStarting
                        ? "Opening..."
                        : isInProgress
                          ? "Continue this"
                          : "Talk about this"}
                    </button>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => onSkipGap(gap)}
                      disabled={loading || isStarting || isSkipping}
                    >
                      {isSkipping ? "Skipping..." : "Skip this"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <p style={styles.mutedCopy}>
            You’ve answered or skipped the current suggested questions. You can
            continue adding stories, or move on to the book plan.
          </p>
        )}
      </section>

      <section style={styles.storyReviewCard}>
        <h3 style={styles.sectionTitle}>Coverage</h3>
        <div style={styles.coverageGrid}>
          {Object.entries(storyReview.coverage).map(([key, value]) => (
            <div key={key} style={styles.coverageItem}>
              <span style={styles.coverageLabel}>
                {coverageLabels[key] || labelFromKey(key)}
              </span>
              <span
                style={{
                  ...styles.coverageBadge,
                  ...coverageBadgeStyles[value],
                }}
              >
                {coverageStatusLabels[value] || value}
              </span>
            </div>
          ))}
        </div>
      </section>

      <div style={styles.bottomActions}>
        <button
          type="button"
          style={styles.secondaryButton}
          onClick={onBackToInterview}
          disabled={loading}
        >
          Continue Interview
        </button>
        <button
          type="button"
          style={styles.primaryButton}
          onClick={onReviewBookPlan}
          disabled={loading}
        >
          {bookPlanLabel}
        </button>
      </div>
    </div>
  );
}

function labelFromKey(key: string) {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const coverageBadgeStyles: Record<string, CSSProperties> = {
  missing: {
    background: "#fef2f2",
    color: "#991b1b",
    borderColor: "#fecaca",
  },
  medium: {
    background: "#fff7ed",
    color: "#9a3412",
    borderColor: "#fed7aa",
  },
  strong: {
    background: "#ecfdf5",
    color: "#166534",
    borderColor: "#bbf7d0",
  },
};

const styles: Record<string, CSSProperties> = {
  storyReviewContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    overflowY: "auto",
    paddingRight: "2px",
  },

  storyReviewHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  storyReviewTitle: {
    margin: 0,
    color: "#111827",
    fontSize: "24px",
    lineHeight: 1.2,
  },

  storyReviewSubtitle: {
    margin: 0,
    color: "#4b5563",
    fontSize: "15px",
    lineHeight: 1.5,
  },

  storyReviewCard: {
    background: "#fff",
    borderRadius: "8px",
    padding: "16px",
    border: "1px solid #e5e7eb",
  },

  progressHeading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    color: "#111827",
    fontSize: "15px",
    lineHeight: 1.4,
  },

  progressOuter: {
    width: "100%",
    height: "10px",
    borderRadius: "999px",
    background: "#e5e7eb",
    overflow: "hidden",
    marginTop: "8px",
    marginBottom: "12px",
  },

  progressInner: {
    height: "100%",
    borderRadius: "999px",
    background: "#4b5563",
  },

  readinessMessage: {
    margin: 0,
    color: "#4b5563",
    fontSize: "14px",
    lineHeight: 1.5,
  },

  progressSummary: {
    margin: "10px 0 0",
    color: "#111827",
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: 1.45,
  },

  debugMutedText: {
    margin: "6px 0 0",
    color: "#6b7280",
    fontSize: "13px",
    lineHeight: 1.4,
  },

  sectionTitle: {
    margin: "0 0 10px",
    color: "#111827",
    fontSize: "18px",
    lineHeight: 1.25,
  },

  storyList: {
    margin: 0,
    paddingLeft: "20px",
    color: "#374151",
    lineHeight: 1.55,
  },

  mutedCopy: {
    margin: 0,
    color: "#6b7280",
    fontSize: "15px",
    lineHeight: 1.5,
  },

  optionalQuestionCopy: {
    margin: "0 0 12px",
    color: "#4b5563",
    fontSize: "15px",
    lineHeight: 1.5,
  },

  storyGapList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  storyGapCard: {
    background: "#fff",
    borderRadius: "8px",
    padding: "16px",
    border: "1px solid #e5e7eb",
  },

  storyGapHeading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    marginBottom: "8px",
  },

  storyGapTitle: {
    margin: 0,
    color: "#111827",
    fontSize: "16px",
    lineHeight: 1.3,
  },

  startedBadge: {
    flex: "0 0 auto",
    border: "1px solid #bfdbfe",
    borderRadius: "999px",
    background: "#eff6ff",
    color: "#1e3a8a",
    padding: "3px 8px",
    fontSize: "12px",
    fontWeight: 700,
    lineHeight: 1.25,
  },

  storyGapQuestion: {
    margin: 0,
    color: "#374151",
    fontSize: "15px",
    lineHeight: 1.5,
  },

  storyGapRationale: {
    margin: "10px 0 0",
    color: "#6b7280",
    fontSize: "14px",
    lineHeight: 1.45,
  },

  storyGapActions: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    marginTop: "12px",
  },

  coverageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "10px",
  },

  coverageItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "10px 12px",
    background: "#f9fafb",
  },

  coverageLabel: {
    minWidth: 0,
    color: "#374151",
    fontSize: "14px",
    lineHeight: 1.35,
  },

  coverageBadge: {
    flex: "0 0 auto",
    border: "1px solid",
    borderRadius: "999px",
    padding: "3px 8px",
    fontSize: "12px",
    fontWeight: 700,
    lineHeight: 1.25,
  },

  bottomActions: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    paddingBottom: "2px",
  },

  primaryButton: {
    padding: "12px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    background: "#222",
    color: "white",
    fontSize: "15px",
    fontWeight: 700,
  },

  secondaryButton: {
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #d1d5db",
    cursor: "pointer",
    background: "#f9fafb",
    color: "#374151",
    fontSize: "15px",
  },
};
